import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import {
  CalendarDays, Send, RefreshCw, Trash2, Eye, Printer, Brain, Loader2,
  CheckCircle, Clock, AlertCircle, ChevronRight, Users, Mail, Plus, X, Download
} from "lucide-react";

const STATUS_CFG = {
  ready:      { label: "Listo",       cls: "bg-green-100 text-green-700",  icon: CheckCircle },
  generating: { label: "Generando…",  cls: "bg-amber-100 text-amber-700",  icon: Clock },
  error:      { label: "Error",       cls: "bg-red-100 text-red-700",      icon: AlertCircle },
};

export default function WeeklySummary() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [savingConfig, setSavingConfig] = useState(false);
  const iframeRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [sRes, cfgRes] = await Promise.all([
        axios.get(`${API}/weekly-summaries`),
        isAdmin ? axios.get(`${API}/settings/weekly-report`) : Promise.resolve({ data: null }),
      ]);
      setSummaries(sRes.data);
      if (cfgRes.data) setSettings(cfgRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  // Poll if any summary is generating
  useEffect(() => {
    const generating = summaries.some(s => s.status === "generating");
    if (generating) {
      pollRef.current = setInterval(async () => {
        const res = await axios.get(`${API}/weekly-summaries`);
        setSummaries(res.data);
        if (!res.data.some(s => s.status === "generating")) clearInterval(pollRef.current);
      }, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [summaries]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/weekly-summaries/generate`);
      setSummaries(prev => [data, ...prev]);
      setViewing(data);
    } catch (e) { alert("Error al generar el resumen"); }
    finally { setGenerating(false); }
  };

  const handleGenerateAndSend = async () => {
    if (!window.confirm("¿Generar y enviar el resumen semanal a los destinatarios configurados?")) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/weekly-summaries/generate-and-send`);
      setSummaries(prev => [data, ...prev]);
      setViewing(data);
    } catch (e) { alert("Error al generar y enviar el resumen"); }
    finally { setGenerating(false); }
  };

  const handleSendExisting = async (wid) => {
    if (!window.confirm("¿Enviar este resumen por email a los destinatarios?")) return;
    setSendingId(wid);
    try {
      const { data } = await axios.post(`${API}/weekly-summaries/${wid}/send`);
      alert(`Enviado a ${data.recipients?.length || 0} destinatario(s)`);
      load();
    } catch (e) { alert("Error al enviar: " + (e.response?.data?.detail || e.message)); }
    finally { setSendingId(null); }
  };

  const handleDelete = async (wid) => {
    if (!window.confirm("¿Eliminar este resumen?")) return;
    await axios.delete(`${API}/weekly-summaries/${wid}`);
    setSummaries(prev => prev.filter(s => s.id !== wid));
    if (viewing?.id === wid) setViewing(null);
  };

  const handleView = async (s) => {
    if (s.html_content) { setViewing(s); return; }
    const { data } = await axios.get(`${API}/weekly-summaries/${s.id}`);
    setViewing(data);
  };

  const handlePrint = () => {
    if (!viewing?.html_content) return;
    const blob = new Blob([viewing.html_content], { type: "text/html; charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (w) w.onload = () => { w.print(); setTimeout(() => URL.revokeObjectURL(url), 5000); }
    else URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async () => {
    if (!viewing?.id) return;
    try {
      const response = await axios.get(`${API}/weekly-summaries/${viewing.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `resumen_semanal_${viewing.id.slice(0,8)}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) { alert("Error al generar el PDF"); }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const { data } = await axios.put(`${API}/settings/weekly-report`, settings);
      setSettings(data);
      setShowConfig(false);
    } catch (e) { alert("Error al guardar configuración"); }
    finally { setSavingConfig(false); }
  };

  const addRecipient = () => {
    if (!newEmail || !newEmail.includes("@")) return;
    setSettings(prev => ({ ...prev, recipients: [...(prev.recipients || []), newEmail] }));
    setNewEmail("");
  };

  const removeRecipient = (email) => {
    setSettings(prev => ({ ...prev, recipients: (prev.recipients || []).filter(e => e !== email) }));
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Resumen Semanal</h1>
          <p className="text-sm text-gray-500 mt-0.5">Generado automáticamente con Pando cada lunes y enviado al Comité de Dirección</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowConfig(true)}
              className="flex items-center gap-2 border border-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
              data-testid="config-btn">
              <Mail size={15} /> Destinatarios
            </button>
            <button onClick={handleGenerate} disabled={generating}
              className="flex items-center gap-2 border border-[#007934] text-[#007934] px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#f0fdf4] disabled:opacity-50"
              data-testid="generate-only-btn">
              {generating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Solo generar
            </button>
            <button onClick={handleGenerateAndSend} disabled={generating}
              className="flex items-center gap-2 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 shadow-sm"
              style={{ background: "#007934" }}
              data-testid="generate-send-btn">
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Generar y enviar
            </button>
          </div>
        )}
      </div>

      {/* Status banner */}
      {settings && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm ${settings.enabled ? "bg-green-50 border-green-200 text-green-800" : "bg-gray-50 border-gray-200 text-gray-600"}`}>
          <div className={`w-2 h-2 rounded-full ${settings.enabled ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
          {settings.enabled
            ? `Envío automático activado: cada lunes a las ${String(settings.send_hour || 8).padStart(2, "0")}:00 — ${(settings.recipients || []).length} destinatario(s)`
            : "Envío automático desactivado — Puedes generar y enviar manualmente"
          }
          {settings.last_sent && (
            <span className="ml-auto text-xs text-gray-400">Último envío: {new Date(settings.last_sent).toLocaleString("es-ES")}</span>
          )}
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* List */}
        <div className="lg:col-span-1 space-y-3">
          <h2 className="text-sm font-semibold text-gray-700">Historial ({summaries.length})</h2>
          {loading ? (
            [...Array(3)].map((_, i) => <div key={i} className="h-24 rounded-xl loading-shimmer" />)
          ) : summaries.length === 0 ? (
            <div className="bg-white rounded-xl border border-dashed border-gray-200 p-8 text-center">
              <Brain size={32} className="text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Sin resúmenes generados</p>
              <p className="text-xs text-gray-400 mt-1">Genera el primero con el botón de arriba</p>
            </div>
          ) : (
            summaries.map(s => {
              const st = STATUS_CFG[s.status] || STATUS_CFG.error;
              const Icon = st.icon;
              return (
                <div key={s.id}
                  className={`bg-white rounded-xl border cursor-pointer transition-all p-4 ${viewing?.id === s.id ? "border-[#007934] shadow-md" : "border-gray-100 hover:border-gray-200"}`}
                  onClick={() => handleView(s)}
                  data-testid={`summary-item-${s.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{s.week_label || "Resumen Semanal"}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${st.cls}`}>
                          <Icon size={10} /> {st.label}
                        </span>
                        {s.sent_to?.length > 0 && (
                          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Send size={10} /> Enviado
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1.5">{s.created_by} · {new Date(s.created_at).toLocaleString("es-ES")}</p>
                      {s.projects_count && (
                        <p className="text-xs text-gray-400">{s.projects_count} proyectos · {s.wi_stats?.open || 0} items abiertos</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      {isAdmin && s.status === "ready" && !s.sent_to?.length && (
                        <button onClick={e => { e.stopPropagation(); handleSendExisting(s.id); }}
                          disabled={sendingId === s.id}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors" title="Enviar por email"
                          data-testid={`send-summary-${s.id}`}>
                          {sendingId === s.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={e => { e.stopPropagation(); handleDelete(s.id); }}
                          className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar"
                          data-testid={`delete-summary-${s.id}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Viewer */}
        <div className="lg:col-span-2">
          {viewing ? (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{viewing.week_label || "Resumen Semanal"}</h3>
                  <p className="text-xs text-gray-400">{new Date(viewing.created_at).toLocaleString("es-ES")} · por {viewing.created_by}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={handlePrint}
                    className="flex items-center gap-2 border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-50"
                    data-testid="print-summary-btn">
                    <Printer size={14} /> Imprimir
                  </button>
                  <button onClick={handleDownloadPdf}
                    className="flex items-center gap-2 bg-[#007934] text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-[#006528]"
                    data-testid="download-pdf-btn">
                    <Download size={14} /> Descargar PDF
                  </button>
                  <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-600">
                    <X size={18} />
                  </button>
                </div>
              </div>
              {viewing.status === "generating" ? (
                <div className="flex flex-col items-center justify-center py-24 gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                    <Brain size={28} className="text-[#007934] animate-pulse" />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-gray-900">Generando resumen con Perplexity AI...</p>
                    <p className="text-sm text-gray-500 mt-1">Analizando proyectos, ofertas, presupuesto y checklists</p>
                  </div>
                  <Loader2 size={28} className="animate-spin text-[#007934]" />
                </div>
              ) : viewing.status === "error" ? (
                <div className="flex flex-col items-center justify-center py-24 text-center gap-2">
                  <AlertCircle size={32} className="text-red-400" />
                  <p className="text-red-500 font-medium">Error al generar el resumen</p>
                  <p className="text-sm text-gray-400">Verifica la configuración de la API de Perplexity</p>
                </div>
              ) : (
                <iframe
                  ref={iframeRef}
                  srcDoc={viewing.html_content}
                  className="w-full border-0"
                  style={{ height: "70vh" }}
                  title="Resumen semanal"
                  data-testid="summary-iframe"
                />
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-dashed border-gray-200 flex flex-col items-center justify-center h-96 gap-4">
              <CalendarDays size={48} className="text-gray-200" />
              <div className="text-center">
                <p className="text-gray-500 font-medium">Selecciona un resumen para verlo</p>
                <p className="text-sm text-gray-400 mt-1">O genera uno nuevo con los botones de arriba</p>
              </div>
              {isAdmin && (
                <button onClick={handleGenerate} disabled={generating}
                  className="flex items-center gap-2 text-white px-4 py-2 rounded-lg text-sm font-medium mt-2 disabled:opacity-50"
                  style={{ background: "#007934" }}>
                  {generating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  Generar ahora
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Config modal */}
      {showConfig && settings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" data-testid="config-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h2 className="text-lg font-bold font-['Outfit']">Configuración de envío automático</h2>
                <p className="text-xs text-gray-400 mt-0.5">Gestiona destinatarios y programación</p>
              </div>
              <button onClick={() => setShowConfig(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Envío automático</p>
                  <p className="text-xs text-gray-400">Cada lunes a las {String(settings.send_hour || 8).padStart(2, "0")}:00</p>
                </div>
                <button onClick={() => setSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.enabled ? "bg-[#007934]" : "bg-gray-200"}`}
                  data-testid="toggle-auto-send">
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.enabled ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>

              {/* Recipients */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Destinatarios ({(settings.recipients || []).length})</label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-100 rounded-lg p-2">
                  {(settings.recipients || []).map(email => (
                    <div key={email} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-sm text-gray-700">{email}</span>
                      <button onClick={() => removeRecipient(email)} className="text-gray-300 hover:text-red-500 transition-colors">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  {(settings.recipients || []).length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-2">Sin destinatarios</p>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <input value={newEmail} onChange={e => setNewEmail(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addRecipient()}
                    placeholder="email@valorizasm.com"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="new-recipient-input" />
                  <button onClick={addRecipient} className="bg-[#007934] text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#006528]"
                    data-testid="add-recipient-btn">
                    <Plus size={14} />
                  </button>
                </div>
              </div>

              <div className="flex gap-2 pt-1">
                <button onClick={() => setShowConfig(false)} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2.5 text-sm font-medium">Cancelar</button>
                <button onClick={handleSaveConfig} disabled={savingConfig}
                  className="flex-1 bg-[#007934] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#006528] disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="save-config-btn">
                  {savingConfig ? <Loader2 size={14} className="animate-spin" /> : null} Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
